import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom, Observable, startWith, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AbstractIdbaModel } from '../models/abstract-idba-model';
import { AbstractIdbaRequestModel } from '../models/abstract-idba-request-model';
import { GenericParameters } from '../models/generic-parameters';
import { ComponentType } from '@angular/cdk/portal';

@Injectable({
  providedIn: 'root',
})
export class AbstractIdbaService {
  parameters: GenericParameters = new GenericParameters();
  _defaultFilterObject: any = {};
  programName: string;
  listProcedure: string;
  filterComponent: ComponentType<any>;
  model: AbstractIdbaModel;
  modelField: { [id: string]: AbstractIdbaModel; } = {};
  modelList: { [id: string]: AbstractIdbaModel[]; } = {};
  modelFilteredList: { [id: string]: Observable<AbstractIdbaModel[]>; } = {};

  constructor(private http: HttpClient, model?: AbstractIdbaModel) {
    this.modelList = {};
    if (model) {
      this.model = model;
    }
    this.resetModel();
  }

  get httpClient(): HttpClient {
    return this.http;
  }

  get defaultFilterObject() {
    return this._defaultFilterObject;
  }

  set defaultFilterObject(data) {
    if (this._defaultFilterObject) {
      this._defaultFilterObject = { ...this._defaultFilterObject, ...data };
    } else {
      this._defaultFilterObject = data;
    }
  }

  prepareModelField(index) {
    if (!this.modelField) {
      this.modelField = {};
    }

    if (!this.modelField[index]) {
      this.modelField[index] = this.model;
    }
  }

  resetModel() {
    this.modelField = {};
    this.modelList = {};
    this.modelFilteredList = {};
    this.parameters = new GenericParameters();
    this.listProcedure = "";
    this._defaultFilterObject = {};
  }

  setModelList(index, registro) {
    this.modelList[index] = [];
    for (let x = 0; x < registro?.length; x++) {
      const element = registro[x];
      this.modelList[index].push(this.model.convertToModel(element))
    }
  }

  setModelFilteredList(index, formControl) {
    const list = this.modelList[index];
    const safeList = Array.isArray(list) ? list : [];
    this.modelFilteredList[index] = formControl.valueChanges.pipe(
      startWith(formControl.value),
      map((value) => {
        if (value === undefined || value === null) {
          return safeList.slice();
        }
        /* Item já selecionado (objeto): não filtrar pelo texto exibido ("2 - UN"), senão a lista some ao abrir a seta */
        if (typeof value === 'object') {
          return safeList.slice();
        }
        const searchStr = value.toString();
        return searchStr
          ? this.modelField[index].filter(searchStr, safeList)
          : safeList.slice();
      })
    );
  }

  saveModel() {
    var request: AbstractIdbaRequestModel = new AbstractIdbaRequestModel();
    request.program = this.programName;
    if (this.model.rowid) {
      request.procedure = 'pi-alterar';
    } else {
      request.procedure = 'pi-incluir';
    }
    request.parameters = { registro: this.model };

    return this.http.post<any>(environment.apiURLIntegrate, request, {
      observe: 'response',
    });
  }

  async listAllByFilter(filterObject?: any) {
    return this.listAll(undefined, undefined, undefined, undefined, undefined, undefined, filterObject);
  }

  async setIdAndFind(index: string, id: number | null, formGroup: any, args?: any, originalClass?: any, identification: string = 'id') {
    if (id) {
      this.modelField[index]['id'] = id;
    }
    this.listProcedure = "pi-pesquisar-por-id";
    this._defaultFilterObject = { id };
    return this.listAll(undefined, formGroup, index, args, originalClass, identification);
  }

  async setIdAndListAll(index: string, id: number | null, formGroup: any, args?: any, originalClass?: any, identification: string = 'id', nextCommand?: any) {
    if (id) {
      this.prepareModelField(index);
      this.modelField[index][identification] = id;
    }
    return this.listAll(nextCommand, formGroup, index, args, originalClass, identification);
  }

  async listAll(nextCommand?: any, formGroup?: any, fieldName?: string, genericoArgs?: [string, string], originalClass?: any, identification: string = 'id', filterObject?: any, setDefaultFirst: boolean = false, setFirstIfOnly: boolean = false, options: any = {}) {
    var index = fieldName ? fieldName : "";

    var request: AbstractIdbaRequestModel = new AbstractIdbaRequestModel();
    request.program = this.programName;
    request.parameters = {};

    if (genericoArgs) {
      request.parameters = { modulo: genericoArgs[0], assunto: genericoArgs[1] };
      if (this.listProcedure) {
        request.procedure = this.listProcedure;
      } else {
        request.procedure = 'pi-listar-modulo-assunto';
      }
    } else {
      request.parameters = filterObject ?? this._defaultFilterObject;
      if (this.listProcedure) {
        request.procedure = this.listProcedure;
      } else {
        request.procedure = 'pi-listar';
      }
    }

    if (this.parameters?.procedure) {
      request.procedure = this.parameters.procedure;
    }

    const response$ = this.http.post<any>(
      environment.apiURLIntegrate,
      request,
      { observe: 'response' }
    );
    const response = await lastValueFrom(response$);

    this.setModelList(index, response?.body?.retorno?.registro);

    var formControl;
    if (formGroup) {
      formControl = formGroup.controls[index];
    }

    let selectedModel:AbstractIdbaModel|null = null;
    if (this.modelField && this.modelField[index] && this.modelField[index][identification] && this.modelField[index][identification] != 0) {
      selectedModel = this.modelList[index].filter((item) => item[identification] == this.modelField[index][identification])[0];
      if (selectedModel) {
        this.modelField[index] = <AbstractIdbaModel>(
          Object.assign(
            this.modelField[index],
            selectedModel
          )
        );
        this.modelField[index].display(this.modelField[index])
        this.model = this.model.convertToModel(this.modelField[index]);
        formControl.setValue(this.model);
      }
    }
    
    if(!selectedModel) {
      if (setDefaultFirst) {
        this.modelField[index] = <AbstractIdbaModel>(
          Object.assign(
            this.modelField[index],
            this.modelList[index][0]
          )
        );

        this.modelField[index].display(this.modelField[index])
        formControl.setValue(this.modelField[index]);
        this.model = this.modelField[index];
      }
      else if (setFirstIfOnly) {
        if (this.modelList[index].length === 1) {
          this.modelField[index] = <AbstractIdbaModel>(
            Object.assign(
              this.modelField[index],
              this.modelList[index][0]
            )
          );

          this.modelField[index].display(this.modelField[index])
          formControl.setValue(this.modelField[index]);
          this.model = this.modelField[index];
        }
      }
    }

    if (nextCommand) {
      nextCommand(originalClass);
    }

    if (formControl) {
      this.setModelFilteredList(index, formControl);
    }

    if (options?.idSelected) {
      const selecionado = this.modelList[index].find((item) => item.id == options.idSelected);
      if (selecionado) {
        formControl.setValue(selecionado);
      }
    }

    return response?.body?.retorno?.registro;
  }

  async customListAll(nextCommand?: any, formGroup?: any, fieldName?: string, procedure?: string, parameters?: any, originalClass?: any, programName?: string) {
    var index = fieldName ? fieldName : "";
    var request: AbstractIdbaRequestModel = new AbstractIdbaRequestModel();
    request.program = programName ?? this.programName;
    request.procedure = procedure ?? 'pi-listar';
    request.parameters = parameters ?? {};
    const response$ = this.http.post<any>(
      environment.apiURLIntegrate,
      request,
      { observe: 'response' }
    );

    const response = await lastValueFrom(response$);
    this.modelList[index] = response?.body?.retorno?.registro ?? [];

    var formControl;
    if (formGroup) {
      formControl = formGroup.controls[index];
    }

    if (this.modelField && this.modelField[index] && this.modelField[index].id && this.modelField[index].id != 0) {
      this.modelField[index] = <AbstractIdbaModel>(
        Object.assign(
          this.modelField[index],
          this.modelList[index].filter((item) => item.id == this.modelField[index].id)[0]
        )
      );
      this.modelField[index].display(this.modelField[index]);
      formControl.setValue(this.modelField[index]);
      this.model = this.modelField[index];
    }

    if (nextCommand) {
      nextCommand(originalClass);
    }

    if (formControl) {
      const listIdx = this.modelList[index];
      const safeIdx = Array.isArray(listIdx) ? listIdx : [];
      this.modelFilteredList[index] = formControl.valueChanges.pipe(
        startWith(formControl.value),
        map((value) => {
          if (value === undefined || value === null) {
            return safeIdx.slice();
          }
          if (typeof value === 'object') {
            return safeIdx.slice();
          }
          const s = value.toString();
          return s ? this.modelField[index].filter(s, safeIdx) : safeIdx.slice();
        })
      );
    }

    return response?.body?.retorno?.registro;
  }

  buscar(parameters?: object) {
    var request: AbstractIdbaRequestModel = new AbstractIdbaRequestModel();
    request.program = this.programName;
    request.procedure = 'pi-listar';
    if (parameters) {
      request.parameters = parameters;
    }
    return this.http.post<any>(environment.apiURLIntegrate, request, {
      observe: 'response',
    });
  }

  listarTodos() {
    var request: AbstractIdbaRequestModel = new AbstractIdbaRequestModel();
    request.program = this.programName;
    request.procedure = 'pi-listar';
    return this.http.post<any>(environment.apiURLIntegrate, request, {
      observe: 'response',
    });
  }

  listarTodosCustom(parameters, procedure: null | string = null) {
    const request: AbstractIdbaRequestModel = new AbstractIdbaRequestModel();
    request.program = this.programName;
    if (procedure) {
      request.procedure = procedure;
    } else {
      request.procedure = 'pi-listar';
    }
    request.parameters = parameters;
    return this.http.post<any>(environment.apiURLIntegrate, request, {
      observe: 'response',
    });
  }

  pesquisar(rowid: string) {
    var request: AbstractIdbaRequestModel = new AbstractIdbaRequestModel();
    request.program = this.programName;
    request.procedure = 'pi-pesquisar';
    request.parameters = { rowid };
    return this.http.post<any>(environment.apiURLIntegrate, request, {
      observe: 'response',
    });
  }

  pesquisarPorId(id: number) {
    var request: AbstractIdbaRequestModel = new AbstractIdbaRequestModel();
    request.program = this.programName;
    request.procedure = 'pi-pesquisar-por-id';
    request.parameters = { id };
    return this.httpClient.post<any>(environment.apiURLIntegrate, request, {
      observe: 'response',
    });
  }

  incluir(registro: any) {
    var request: AbstractIdbaRequestModel = new AbstractIdbaRequestModel();
    request.program = this.programName;
    request.procedure = 'pi-incluir';
    request.parameters = { registro };
    return this.http.post<any>(environment.apiURLIntegrate, request, {
      observe: 'response',
    });
  }

  alterar(registro: any) {
    var request: AbstractIdbaRequestModel = new AbstractIdbaRequestModel();
    request.program = this.programName;
    request.procedure = 'pi-alterar';
    request.parameters = { registro };
    return this.http.post<any>(environment.apiURLIntegrate, request, {
      observe: 'response',
    });
  }

  excluir(rowid: string) {
    var request: AbstractIdbaRequestModel = new AbstractIdbaRequestModel();
    request.program = this.programName;
    request.procedure = 'pi-excluir';
    request.parameters = { rowid };
    return this.http.post<any>(environment.apiURLIntegrate, request, {
      observe: 'response',
    });
  }

  customExcluir(params: any, procedure?: string) {
    var request: AbstractIdbaRequestModel = new AbstractIdbaRequestModel();
    request.program = this.programName;
    request.procedure = procedure ?? 'pi-excluir';
    request.parameters = params;
    return this.http.post<any>(environment.apiURLIntegrate, request, {
      observe: 'response',
    });
  }
}
